import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SelectField } from "./Form";
import { Button, Pill, Section, useAuth, useToast } from "@ozpanel/ui";

// DmarcPanel — a mail domain's DMARC policy (dmarc.go). ONE component for the admin and the account
// console: the same route, scoped by the server.
//
// Shows the setting beside what it governs (rule 1): the record the zone SERVES (PowerDNS asked, not the
// row), whether that is the record the setting renders, whether the report address delivers, and why a
// quarantine or reject policy may catch the domain's own mail.

export interface DmarcStatus {
  domain: string;
  /** The stored setting: "" means the platform default. */
  policy: string;
  effective: string;
  default: string;
  choices: string[];
  rendered: string;
  served?: string | null;
  served_policy?: string;
  served_note?: string;
  in_force: boolean;
  /** false: the zone here is not the one the internet reads (delegated elsewhere). */
  delegated_here?: boolean;
  rua: string;
  rua_deliverable: boolean;
  rua_note?: string;
  risks?: string[];
  risks_known: boolean;
  publish_error?: string;
}

const HELP: Record<string, string> = {
  none: "Receivers only report; mail that fails DMARC is delivered as usual.",
  quarantine: "Mail that fails DMARC goes to the receiver's spam folder.",
  reject: "Mail that fails DMARC is refused outright.",
};

export function DmarcPanel({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const st = useQuery({
    queryKey: ["md-dmarc", domain],
    queryFn: () => client.apiFetch<DmarcStatus>(`/mail/domains/${encodeURIComponent(domain)}/dmarc`),
    retry: false,
  });
  const [draft, setDraft] = React.useState<string | null>(null);
  const save = useMutation({
    mutationFn: (policy: string) =>
      client.apiFetch<DmarcStatus>(`/mail/domains/${encodeURIComponent(domain)}/dmarc`, {
        method: "PUT",
        body: JSON.stringify({ policy }),
      }),
    onSuccess: (d) => {
      setDraft(null);
      if (d?.publish_error) {
        notify({ kind: "error", title: "Saved, not published yet", body: d.publish_error });
      } else {
        notify({ kind: "success", title: "DMARC policy published" });
      }
      void qc.invalidateQueries({ queryKey: ["md-dmarc", domain] });
    },
    onError: (e) => notify({ kind: "error", title: "DMARC policy not saved", body: e instanceof Error ? e.message : String(e) }),
  });

  const d = st.data;
  const value = draft ?? d?.policy ?? "";
  const options = [
    { value: "", label: `Platform default (${d?.default ?? "quarantine"})`, help: HELP[d?.default ?? "quarantine"] },
    ...(d?.choices ?? ["none", "quarantine", "reject"]).map((c) => ({ value: c, label: c, help: HELP[c] })),
  ];
  const risks = d?.risks ?? [];
  return (
    <Section
      title="DMARC"
      hint={d ? `${d.effective}${d.in_force ? "" : " · not in force"}` : undefined}
    >
      <div className="space-y-3 text-[12.5px]">
        {st.error && <p className="text-crit">{String((st.error as Error).message)}</p>}
        {d && (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-72">
                <SelectField
                  label="Policy"
                  value={value}
                  onChange={setDraft}
                  options={options}
                  consequence={
                    (value || d.default) !== "none" && risks.length > 0
                      ? "This domain has senders that do not sign with this platform's key (listed below); their mail may be quarantined or refused."
                      : undefined
                  }
                />
              </div>
              <Button size="sm" variant="accent" disabled={draft === null || save.isPending} onClick={() => save.mutate(value)}>
                Save
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted">Served by the zone:</span>
              <Pill tone={d.in_force ? "ok" : "warn"}>{d.in_force ? "in force" : "not in force"}</Pill>
              <code className="break-all font-mono text-[12px]">{d.served || "nothing"}</code>
            </div>
            {d.served_note && <p className="text-muted">{d.served_note}</p>}
            {!d.in_force && (
              <p className="text-muted">
                Will publish: <code className="break-all font-mono text-[12px]">{d.rendered}</code>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted">Reports go to</span>
              <code className="font-mono">{d.rua}</code>
              <Pill tone={d.rua_deliverable ? "ok" : "warn"}>{d.rua_deliverable ? "delivers" : "not published"}</Pill>
            </div>
            {d.rua_note && <p className="text-muted">{d.rua_note}</p>}
            <div>
              <div className="font-medium">Senders a strict policy may catch</div>
              {!d.risks_known && <p className="text-warn">Could not be read from the zone, so nothing can be said.</p>}
              {d.risks_known && risks.length === 0 && (
                <p className="text-muted">None found: only this platform signs and sends for this domain.</p>
              )}
              {risks.length > 0 && (
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {risks.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

export interface DmarcRiskRow {
  domain: string;
  policy: string;
  served: string;
  risks: string[];
  unknown?: string;
}

/** The platform list (super_admin): every mail domain whose own mail a strict policy may catch. */
export function DmarcRiskList() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["mail-dmarc-risks"],
    queryFn: () =>
      client.apiFetch<{ domains: number; clear: number; at_risk: DmarcRiskRow[]; default: string }>("/mail/dmarc/risks"),
    retry: false,
  });
  const rows = q.data?.at_risk ?? [];
  return (
    <Section title="DMARC risk" hint={q.data ? `${rows.length} of ${q.data.domains}` : undefined}>
      <div className="space-y-2 text-[12.5px]">
        {q.error && <p className="text-crit">{String((q.error as Error).message)}</p>}
        {q.data && rows.length === 0 && <p className="text-muted">No mail domain has another signer or sender.</p>}
        {rows.map((r) => (
          <div key={r.domain}>
            <div className="flex items-center gap-2">
              <span className="font-mono">{r.domain}</span>
              <Pill tone={r.unknown ? "neutral" : "warn"}>{r.unknown ? "unknown" : r.policy}</Pill>
            </div>
            {r.unknown && <p className="text-muted">{r.unknown}</p>}
            <ul className="list-disc pl-5">
              {r.risks.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}
